import React, { useState } from 'react';
import { Tag, message, Button, Form, Input } from 'antd';
import TabularList from 'components/TabularList/TabularList';
import { Add } from '@carbon/icons-react';
import dayjs from 'dayjs';
import { DATE_FORMAT_US_SLASH } from 'components/AriaDatePicker/AriaDatePicker';
import providerService from 'services/providers.service';
import Card, { CardDelete, CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import { ENDASH } from 'constants';
import AsyncSelect from 'components/Select/AsyncSelect';
import { DatePicker } from 'components/ui/DatePicker';
import Switch from 'components/Switch/Switch';
import UploadFile from 'components/UploadFile/UploadFile';
import { PROVIDER_FILE_TYPES } from 'services/providers.service';

const AccreditationCard = ({ cred, idx, isEditing, form, application, onDelete }) => {
  const neverExpires = Form.useWatch(`neverExpires_${idx}`, form);

  return (
    <Card
      key={cred?.id || `new-${idx}`}
      className="mb-4"
      collapsible
      title={cred?.id ? cred?.type?.description || ENDASH : 'Unsaved'}
      subtitle={cred?.id ? `#${cred?.accreditationNumber || ''}` : ENDASH}
      extra={isEditing ? <CardDelete onConfirm={() => onDelete(idx)} /> : null}
      noBodyPadding
    >
      <TabularList
        indented
        data={[
          {
            title: 'Accrediting body',
            value: isEditing ? (
              <Form.Item
                name={`type_${idx}`}
                className="mb-0"
                rules={[{ required: true, message: 'Accrediting body is required' }]}
              >
                <AsyncSelect
                  optionsApiUrl={`/accreditation-types?typeOfCare=${application?.careFacility?.careFacilityType?.id}`}
                  getOptionLabel={(option) =>
                    option.description
                      ? `${option.description}${option.title ? ` (${option.title})` : ''}`
                      : option.title
                  }
                  getOptionValue={(option) => option.id}
                  isOptionSelected={(option, [value]) => option?.id === (value?.id || value)}
                />
              </Form.Item>
            ) : (
              cred.type.description
            ),
            titleRequired: true,
          },
          {
            title: 'Credential number',
            value: isEditing ? (
              <Form.Item
                name={`accreditationNumber_${idx}`}
                className="mb-0"
                rules={[{ required: true, message: 'Credential number is required' }]}
              >
                <Input placeholder="Enter credential number" className="max-w-72" />
              </Form.Item>
            ) : (
              cred.accreditationNumber
            ),
          },
          {
            title: 'Start date',
            value: isEditing ? (
              <Form.Item
                name={`startDate_${idx}`}
                className="mb-0"
                rules={[{ required: true, message: 'Start date is required' }]}
              >
                <DatePicker className="max-w-72" />
              </Form.Item>
            ) : (
              dayjs(cred.startDate).format(DATE_FORMAT_US_SLASH)
            ),
          },
          ...((neverExpires === undefined ? cred.neverExpires : neverExpires)
            ? []
            : [
                {
                  title: 'End date',
                  value: isEditing ? (
                    <Form.Item
                      name={`endDate_${idx}`}
                      className="mb-0"
                      rules={[{ required: true, message: 'End date is required' }]}
                    >
                      <DatePicker className="max-w-72" />
                    </Form.Item>
                  ) : (
                    dayjs(cred.endDate).format(DATE_FORMAT_US_SLASH)
                  ),
                },
              ]),
          {
            title: 'Expiry',
            value: isEditing ? (
              <Form.Item
                name={`neverExpires_${idx}`}
                className="mb-0"
                valuePropName="checked"
                getValueFromEvent={(checked) => !checked}
                getValueProps={(value) => ({ checked: !value })}
              >
                <Switch checkedChildren="Expires" unCheckedChildren="Never expires" />
              </Form.Item>
            ) : cred.neverExpires ? (
              'Does not expire'
            ) : (
              'Expires'
            ),
          },
        ]}
      />
    </Card>
  );
};

const Accreditations = ({ application, applicationFiles, setApplicationFiles, isEditable = false }) => {
  const [localAccreditations, setLocalAccreditations] = useState(application?.accreditations || []);
  const [deletedAccreditations, setDeletedAccreditations] = useState([]);
  const [form] = Form.useForm();

  const { isEditing, editProps } = useEditCard({
    id: 'accreditations',
    onEditStart: () => {
      const formValues = {};
      localAccreditations.forEach((cred, idx) => {
        formValues[`type_${idx}`] = cred.type.id;
        formValues[`accreditationNumber_${idx}`] = cred.accreditationNumber;
        formValues[`startDate_${idx}`] = cred.startDate ? dayjs(cred.startDate).format('YYYY-MM-DD') : '';
        formValues[`endDate_${idx}`] = cred.endDate ? dayjs(cred.endDate).format('YYYY-MM-DD') : '';
        formValues[`neverExpires_${idx}`] = cred.neverExpires;
      });
      form.setFieldsValue(formValues);
    },
    onSave: async () => {
      await form.validateFields();
      const values = form.getFieldsValue();
      const deletePromises = deletedAccreditations.map(async (accreditation) => {
        if (accreditation.id) return providerService.deleteAccreditation(application.id, accreditation.id);
      });
      const updatePromises = localAccreditations.map(async (accreditation, idx) => {
        const payload = {
          ...(accreditation.id ? { id: accreditation.id } : {}),
          type: typeof values[`type_${idx}`] === 'number' ? { id: values[`type_${idx}`] } : values[`type_${idx}`],
          accreditationNumber: values[`accreditationNumber_${idx}`],
          startDate: values[`startDate_${idx}`]
            ? new Date(values[`startDate_${idx}`]).toISOString()
            : accreditation.startDate,
          endDate: values[`endDate_${idx}`] ? new Date(values[`endDate_${idx}`]).toISOString() : accreditation.endDate,
          neverExpires: values[`neverExpires_${idx}`] || false,
        };
        return providerService.addUpdateAccreditation(application.id, payload);
      });
      await Promise.all(deletePromises);
      await Promise.all(updatePromises);
      const refreshedApplication = await providerService.getApplication(application.id);
      setLocalAccreditations(refreshedApplication.accreditations || []);
      setDeletedAccreditations([]);
      form.resetFields();
      message.success('Accreditations updated');
    },
    onDiscard: () => {
      const savedAccreditations = localAccreditations.filter((acc) => acc.id !== null);
      setLocalAccreditations([...savedAccreditations, ...deletedAccreditations]);
      setDeletedAccreditations([]);
      form.resetFields();
    },
  });

  const handleDeleteAccreditation = (accreditationIndex) => {
    const accreditation = localAccreditations[accreditationIndex];

    // Add to deleted list if it has an ID (existing accreditation)
    if (accreditation.id) {
      setDeletedAccreditations([...deletedAccreditations, accreditation]);
    }

    // Remove from local list
    const updatedAccreditations = localAccreditations.filter((_, idx) => idx !== accreditationIndex);
    setLocalAccreditations(updatedAccreditations);
  };

  const handleAddAccreditation = () => {
    const newAccreditation = {
      id: null, // Temporary ID for new accreditations
      type: { id: 1, description: '' },
      accreditationNumber: '',
      startDate: new Date().toISOString(),
      endDate: new Date().toISOString(),
      neverExpires: false,
    };

    const updatedAccreditations = [...localAccreditations, newAccreditation];
    setLocalAccreditations(updatedAccreditations);

    // Set form values for the new accreditation
    const idx = updatedAccreditations.length - 1;
    form.setFieldsValue({
      [`type_${idx}`]: 1,
      [`accreditationNumber_${idx}`]: '',
      [`startDate_${idx}`]: dayjs().format('YYYY-MM-DD'),
      [`endDate_${idx}`]: dayjs().format('YYYY-MM-DD'),
      [`neverExpires_${idx}`]: false,
    });
  };

  return (
    <Card
      title={
        <div className="flex items-center gap-3">
          <span>Accreditations</span>

          <Tag>{localAccreditations.length}</Tag>
        </div>
      }
      extra={isEditable ? <CardEdit {...editProps} /> : null}
      stickyHeader
    >
      <Form form={form} disabled={editProps.loading}>
        {localAccreditations.length > 0 &&
          localAccreditations.map((cred, idx) => (
            <AccreditationCard
              key={cred?.id || `new-${idx}`}
              cred={cred}
              idx={idx}
              isEditing={isEditing}
              form={form}
              application={application}
              onDelete={handleDeleteAccreditation}
            />
          ))}

        {isEditing && isEditable && (
          <Button
            color="default"
            variant="dashed"
            onClick={handleAddAccreditation}
            icon={<Add size={20} />}
            block
            className="mt-4"
            disabled={editProps.loading}
          >
            Add accreditation
          </Button>
        )}

        {/* Accreditation Documents Card */}
        <Card title="Accreditation documents" className="mt-4" noBodyPadding>
          <TabularList
            indented
            data={[
              {
                title: 'Accreditation files',
                value: (
                  <UploadFile
                    fileCode={PROVIDER_FILE_TYPES.NATIONAL_ACCREDITATION.code}
                    files={applicationFiles}
                    setFiles={setApplicationFiles}
                    applicationId={application?.id}
                    readOnly={!isEditable}
                  />
                ),
              },
            ]}
          />
        </Card>
      </Form>
    </Card>
  );
};

export default Accreditations;
